<template>
  <div
      v-if="globalPlayer.current"
      class="global-player d-flex align-items-center"
  >
    <b-button
        :variant="globalPlayer.error ? 'flat-danger' : 'flat-primary'"
        class="btn-icon rounded-circle mr-50"
        size="sm"
        :title="playButtonTitle"
        @click="togglePlayback"
    >
      <feather-icon :icon="globalPlayer.isPlaying ? 'PauseIcon' : 'PlayIcon'" size="16" />
    </b-button>

    <div class="global-player-info d-none d-md-block mr-1">
      <div class="text-truncate font-weight-bold">
        <b-link
            v-if="globalPlayer.current.link"
            :to="globalPlayer.current.link"
            class="text-body"
        >
          {{ headline }}
        </b-link>
        <span v-else>{{ headline }}</span>
      </div>
      <div
          v-if="globalPlayer.error"
          class="text-truncate text-danger font-small-2 d-none d-lg-block"
      >
        {{ globalPlayer.error }}
      </div>
      <div
          v-else
          class="text-truncate text-muted font-small-2 d-none d-lg-block"
      >
        <span v-if="detailsLine">{{ detailsLine }}</span>
        <feather-icon
            v-if="globalPlayer.current.hint"
            v-b-tooltip.hover
            :title="globalPlayer.current.hint"
            icon="InfoIcon"
            size="12"
            class="ml-25 align-middle cursor-pointer"
        />
        <b-badge
            v-if="globalPlayer.current.sourceLabel"
            variant="light-secondary"
            class="ml-50 global-player-badge"
        >
          {{ globalPlayer.current.sourceLabel }}
        </b-badge>
      </div>
    </div>

    <input
        type="range"
        class="global-player-seek"
        min="0"
        step="0.1"
        :max="globalPlayer.duration || 0"
        :value="seekPosition"
        :disabled="!globalPlayer.duration"
        aria-label="Перемотка"
        :aria-valuetext="formatTime(seekPosition)"
        @input="onSeekInput"
        @change="onSeekCommit"
    >
    <span class="global-player-time text-muted font-small-2 ml-50 d-none d-md-inline">
      {{ formatTime(seekPosition) }} / {{ formatTime(globalPlayer.duration) }}
    </span>

    <b-button
        variant="flat-secondary"
        class="btn-icon ml-25"
        size="sm"
        title="Закрыть плеер"
        @click="closePlayer"
    >
      <feather-icon icon="XIcon" size="14" />
    </b-button>
  </div>
</template>

<script>
import {
  BButton, BBadge, BLink, VBTooltip,
} from 'bootstrap-vue'
import { computed, ref } from '@vue/composition-api'
import {
  globalPlayer, toggleTrack, seekTo, closePlayer,
} from './globalPlayer'

export default {
  components: {
    BButton,
    BBadge,
    BLink,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup() {
    const headline = computed(() => {
      const current = globalPlayer.current

      if (!current) return ''

      return current.artist ? `${current.artist} — ${current.title}` : current.title
    })

    const detailsLine = computed(() => {
      const details = (globalPlayer.current && globalPlayer.current.details) || []

      return details.filter(Boolean).join(' · ')
    })

    // При ошибке подсказка кнопки показывает её текст — на узком экране это
    // единственное место, где ошибку можно увидеть
    const playButtonTitle = computed(() => {
      if (globalPlayer.error) return globalPlayer.error

      return globalPlayer.isPlaying ? 'Пауза' : 'Играть'
    })

    // Кнопка плеера управляет тем же треком, что уже загружен
    const togglePlayback = () => {
      if (globalPlayer.current) toggleTrack(globalPlayer.current)
    }

    // Пока ползунок перетаскивают, он показывает своё значение, а не позицию
    // воспроизведения: иначе timeupdate отбрасывал бы его назад прямо под курсором
    const isSeeking = ref(false)
    const seekPreview = ref(0)

    const seekPosition = computed(() => (isSeeking.value ? seekPreview.value : globalPlayer.currentTime))

    const onSeekInput = event => {
      isSeeking.value = true
      seekPreview.value = Number(event.target.value)
    }

    // Перемотка применяется один раз — когда ползунок отпустили
    const onSeekCommit = event => {
      seekTo(Number(event.target.value))
      isSeeking.value = false
    }

    const formatTime = seconds => {
      const totalSeconds = Math.floor(seconds || 0)
      const minutes = Math.floor(totalSeconds / 60)
      const rest = totalSeconds % 60

      return `${minutes}:${String(rest).padStart(2, '0')}`
    }

    return {
      globalPlayer,
      headline,
      detailsLine,
      playButtonTitle,
      togglePlayback,
      seekPosition,
      onSeekInput,
      onSeekCommit,
      formatTime,
      closePlayer,
    }
  },
}
</script>

<style lang="scss" scoped>
// Плеер может сжиматься: при нехватке места в шапке уступает он, а не иконки справа
.global-player {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 560px;
  padding: 0 0.5rem;
}

// text-truncate внутри flex работает только при ограниченной ширине
.global-player-info {
  min-width: 0;
  max-width: 260px;
  line-height: 1.2;
}

.global-player-seek {
  flex: 0 1 140px;
  width: 140px;
  min-width: 60px;
  cursor: pointer;
}

.global-player-time {
  white-space: nowrap;
}

.global-player-badge {
  font-size: 0.65rem;
}
</style>
